import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Card,
  CardContent,
  Chip,
  Divider
} from '@mui/material';
import {
  Star as StarIcon,
  Schedule as ScheduleIcon,
  DateRange as DateRangeIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';

interface SubscriptionData {
  status: string;
  plan: string;
  trialEndsAt?: any;                 // Trial bitiş tarihi
  stripePlan?: string;
  currentPeriodEnd?: any;
  paymentMethod?: string;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
  cancelAtPeriodEnd?: boolean;
  activatedAt?: any;
}

interface ProIndicatorProps {
  subscription: SubscriptionData | null;
  loading?: boolean;
}

export default function ProIndicator({ subscription, loading }: ProIndicatorProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  if (loading) {
    return null;
  }

  const isActive = subscription?.status === 'active';
  const isInactive = subscription?.status === 'inactive';
  const isPro = subscription?.plan === 'pro';
  const isTrial = subscription?.plan === 'trial';
  const hasActiveProSubscription = isPro && isActive;
  const hasActiveTrialSubscription = isTrial && isActive;

  // Calculate days remaining for trial
  const getDaysRemaining = () => {
    if (!subscription?.trialEndsAt) return 0;
    const trialEnd = subscription.trialEndsAt.toDate ? subscription.trialEndsAt.toDate() : new Date(subscription.trialEndsAt);
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Get next billing date (matches countdown logic)
  const getNextBillingDate = () => {
    // If trial is active, next billing is at trial end + paid period
    if (subscription?.trialEndsAt) {
      try {
        let trialEnd: Date;

        if (subscription.trialEndsAt.toDate) {
          trialEnd = subscription.trialEndsAt.toDate();
        } else if (typeof subscription.trialEndsAt === 'number') {
          trialEnd = new Date(subscription.trialEndsAt * 1000);
        } else {
          trialEnd = new Date(subscription.trialEndsAt);
        }

        const now = new Date();
        const trialDaysRemaining = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        // If trial is still active, calculate next billing after paid period
        if (trialDaysRemaining > 0) {
          const getPaidPeriodDays = (stripePlan: string) => {
            switch (stripePlan) {
              case 'monthly': return 30;
              case 'quarterly': return 90;
              case 'semiannual': return 180;
              case 'annual': return 365;
              default: return 30;
            }
          };

          const paidDays = getPaidPeriodDays(subscription.stripePlan || 'monthly');
          const nextBilling = new Date(trialEnd.getTime() + (paidDays * 24 * 60 * 60 * 1000));
          return nextBilling.toLocaleDateString();
        }
      } catch (error) {
        console.error('Error calculating next billing from trial:', error);
      }
    }

    // If no trial or trial expired, use currentPeriodEnd
    if (subscription?.currentPeriodEnd) {
      try {
        const endDate = subscription.currentPeriodEnd.toDate ?
          subscription.currentPeriodEnd.toDate() :
          new Date(subscription.currentPeriodEnd);
        return endDate.toLocaleDateString();
      } catch (error) {
        console.error('Error parsing currentPeriodEnd for billing:', error);
      }
    }

    return null;
  };

  // Get plan display name
  const getPlanDisplayName = () => {
    if (isTrial) return 'Free Trial';
    if (hasActiveProSubscription && subscription?.stripePlan) {
      const planNames: { [key: string]: string } = {
        monthly: 'Pro Monthly',
        quarterly: 'Pro Quarterly',
        semiannual: 'Pro Semi-Annual',
        annual: 'Pro Annual'
      };
      return planNames[subscription.stripePlan] || 'Pro Plan';
    }
    return 'Trial';
  };

  const daysRemaining = getDaysRemaining();
  const nextBillingDate = getNextBillingDate();

  // Get combined trial + paid days remaining for Pro subscription
  const getProDaysRemaining = () => {
    console.log('🔍 DEBUG ProIndicator - Full subscription object:', subscription);
    console.log('🔍 DEBUG ProIndicator - currentPeriodEnd raw:', subscription?.currentPeriodEnd);
    console.log('🔍 DEBUG ProIndicator - trialEndsAt raw:', subscription?.trialEndsAt);

    const now = new Date();

    // Check if we have trial information
    if (subscription?.trialEndsAt) {
      try {
        let trialEnd: Date;

        // Handle Firebase Timestamp for trial
        if (subscription.trialEndsAt.toDate) {
          trialEnd = subscription.trialEndsAt.toDate();
        } else if (typeof subscription.trialEndsAt === 'number') {
          // Handle seconds timestamp
          trialEnd = new Date(subscription.trialEndsAt * 1000);
        } else {
          trialEnd = new Date(subscription.trialEndsAt);
        }

        console.log('🔍 DEBUG ProIndicator - Parsed trialEnd:', trialEnd);

        // Calculate trial days remaining
        const trialDiffTime = trialEnd.getTime() - now.getTime();
        const trialDaysRemaining = Math.ceil(trialDiffTime / (1000 * 60 * 60 * 24));

        console.log('🔍 DEBUG ProIndicator - Trial days remaining:', trialDaysRemaining);

        // If trial is still active, add paid period days
        if (trialDaysRemaining > 0) {
          // Get paid period length based on plan
          const getPaidPeriodDays = (stripePlan: string) => {
            switch (stripePlan) {
              case 'monthly': return 30;
              case 'quarterly': return 90;
              case 'semiannual': return 180;
              case 'annual': return 365;
              default: return 30;
            }
          };

          const paidDays = getPaidPeriodDays(subscription.stripePlan || 'monthly');
          const totalDays = trialDaysRemaining + paidDays;

          console.log('✅ DEBUG ProIndicator - Trial active, paid days:', paidDays);
          console.log('✅ DEBUG ProIndicator - Total combined days:', totalDays);

          return Math.max(0, totalDays);
        }
      } catch (error) {
        console.error('❌ DEBUG ProIndicator - Error parsing trial date:', error);
      }
    }

    // If no trial or trial expired, use currentPeriodEnd (normal paid subscription)
    if (subscription?.currentPeriodEnd) {
      try {
        let periodEnd: Date;

        // Handle Firebase Timestamp
        if (subscription.currentPeriodEnd.toDate) {
          periodEnd = subscription.currentPeriodEnd.toDate();
        } else if (typeof subscription.currentPeriodEnd === 'number') {
          periodEnd = new Date(subscription.currentPeriodEnd * 1000);
        } else {
          periodEnd = new Date(subscription.currentPeriodEnd);
        }

        const diffTime = periodEnd.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        console.log('✅ DEBUG ProIndicator - Using currentPeriodEnd, days:', diffDays);

        return Math.max(0, diffDays);
      } catch (error) {
        console.error('❌ DEBUG ProIndicator - Error parsing currentPeriodEnd:', error);
      }
    }

    console.log('❌ DEBUG ProIndicator - No valid period data found');
    return 0;
  };

  const TooltipContent = () => (
    <Card sx={{ minWidth: 250, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
      <CardContent sx={{ p: 2 }}>
        {/* Plan Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {hasActiveProSubscription ? (
            <StarIcon sx={{ color: '#10b981', fontSize: 20 }} />
          ) : (
            <ScheduleIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {getPlanDisplayName()}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Status Details */}
        {isTrial && isActive && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ScheduleIcon sx={{ color: '#64748b', fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                Trial Remaining
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: daysRemaining > 2 ? '#10b981' : '#f59e0b' }}>
              {daysRemaining} days
            </Typography>
          </Box>
        )}

        {hasActiveProSubscription && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <DateRangeIcon sx={{ color: '#64748b', fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                Subscription Status
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>
              {getProDaysRemaining()} days remaining
            </Typography>

            {/* Show trial info if trial is active */}
            {(() => {
              const now = new Date();
              if (subscription?.trialEndsAt) {
                let trialEnd: Date;
                try {
                  if (subscription.trialEndsAt.toDate) {
                    trialEnd = subscription.trialEndsAt.toDate();
                  } else if (typeof subscription.trialEndsAt === 'number') {
                    trialEnd = new Date(subscription.trialEndsAt * 1000);
                  } else {
                    trialEnd = new Date(subscription.trialEndsAt);
                  }

                  const trialDaysRemaining = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

                  if (trialDaysRemaining > 0) {
                    const getPaidDays = (plan: string) => {
                      switch (plan) {
                        case 'monthly': return 30;
                        case 'quarterly': return 90;
                        case 'semiannual': return 180;
                        case 'annual': return 365;
                        default: return 30;
                      }
                    };
                    const paidDays = getPaidDays(subscription.stripePlan || 'monthly');

                    return (
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                        Including {trialDaysRemaining} trial + {paidDays} paid days
                      </Typography>
                    );
                  }
                } catch (error) {
                  console.error('Error parsing trial date in tooltip:', error);
                }
              }

              return (
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Next billing: {nextBillingDate || 'N/A'}
                </Typography>
              );
            })()}
          </Box>
        )}


        {/* Status Chip */}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Chip
            label={subscription?.status?.toUpperCase() || 'INACTIVE'}
            size="small"
            sx={{
              background: isActive
                ? 'linear-gradient(135deg, #10b981, #059669)'
                : 'linear-gradient(135deg, #64748b, #475569)',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.6rem',
              letterSpacing: '0.5px'
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );

  if (!subscription) {
    return null;
  }

  return (
    <Tooltip
      title={<TooltipContent />}
      open={tooltipOpen}
      onOpen={() => setTooltipOpen(true)}
      onClose={() => setTooltipOpen(false)}
      arrow
      placement="bottom-end"
      PopperProps={{
        sx: {
          '& .MuiTooltip-tooltip': {
            backgroundColor: 'transparent',
            padding: 0,
            maxWidth: 'none'
          }
        }
      }}
    >
      <Box
        sx={{
          background: hasActiveProSubscription
            ? 'linear-gradient(135deg, #10b981, #059669)'  // Green for PRO
            : hasActiveTrialSubscription
            ? 'linear-gradient(135deg, #f59e0b, #d97706)'  // Orange for active TRIAL
            : 'linear-gradient(135deg, #64748b, #475569)', // Gray for INACTIVE
          px: 2,
          py: 0.5,
          borderRadius: '16px',
          ml: 2,
          mr: 2,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          }
        }}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
      >
        <Typography
          sx={{
            color: '#ffffff',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}
        >
          {hasActiveProSubscription ? 'PRO' :
           hasActiveTrialSubscription ? 'TRIAL' :
           'INACTIVE'}
        </Typography>
      </Box>
    </Tooltip>
  );
}